import type { ReactNode } from 'react';
import { formatLong } from '@/lib/utils/dates';

/**
 * Page header with two render modes:
 *
 * 1. Simple — a title with an "As of <date>" stamp.
 * 2. Hero — a dark-bg banner with a large title, subtitle, description,
 *    action buttons, and a spiral background image (pass `heroTitle`).
 */

export interface PageHeaderAction {
  url?: string;
  /** Alias for `url`. */
  href?: string;
  text?: ReactNode;
  /** Alias for `text`. */
  label?: ReactNode;
  active?: boolean;
  className?: string;
}

export interface PageHeaderProps {
  /** Small title rendered above a date stamp. */
  title?: ReactNode;
  /** Small subtitle rendered under the title. */
  subtitle?: ReactNode;
  /** Optional short description rendered under the title. */
  description?: ReactNode;
  /** Beta badge next to the title. */
  beta?: boolean;
  /** Optional right-aligned action node or action list. */
  actions?: ReactNode | PageHeaderAction[] | null;
  /** Shown on the right of the title as "As of <date>". Defaults to today. */
  asOfDate?: Date;
  /** Hero-style large title. When present, renders the hero layout. */
  heroTitle?: ReactNode;
  heroSubtitle?: ReactNode;
  heroDescription?: ReactNode;
  heroBeta?: boolean;
  heroActions?: PageHeaderAction[];
  /** Local path under `/static/` (e.g. `/static/logos/spiral.svg`). */
  heroImage?: string;
  heroImageAlt?: string;
  className?: string;
}

// Local fallback. `/static/logos/image.png` ships with the portal (used by
// the nav logo) and is served by Flask's static route.
const LOCAL_HERO_IMAGE =
  'https://fulcrumasset.com/wp-content/uploads/2024/08/Fulcrum-hero-spiral.svg';

// Beta badge. The `sm` variant is used in page headers; the `lg` variant is
// used inside the hero title.
const BETA_BADGE_BASE =
  'inline-block align-super font-semibold text-[color:var(--accent)] ' +
  'bg-[rgba(78,183,172,0.15)] border border-[rgba(78,183,172,0.3)] rounded-[3px] ' +
  'uppercase tracking-[0.02em] leading-none relative';

const BETA_BADGE_SIZE = {
  sm: 'ml-[0.3rem] text-[0.65rem] px-[0.1rem] py-[0.35rem] -top-[0.3em]',
  lg: 'ml-[0.4rem] text-[0.85rem] px-[0.15rem] py-[0.4rem] -top-[0.5em]',
} as const;

export function BetaBadge({ size = 'sm' }: { size?: keyof typeof BETA_BADGE_SIZE }): JSX.Element {
  return <sup className={`${BETA_BADGE_BASE} ${BETA_BADGE_SIZE[size]}`}>Beta</sup>;
}

function renderActions(actions: PageHeaderProps['actions']): ReactNode {
  if (!actions) return null;
  if (Array.isArray(actions)) {
    return actions.map((a, i) => {
      const href = a.url ?? a.href ?? '#';
      const text = a.text ?? a.label ?? '';
      return (
        <a
          key={`${href}-${i}`}
          href={href}
          className={[
            // Pill-shaped action button.
            'inline-flex items-center rounded-full px-4 py-2 text-sm font-medium transition-colors no-underline',
            a.active
              ? 'bg-[color:var(--accent-hover)] text-[color:var(--text-light)] border border-[color:var(--accent-hover)]'
              : 'bg-[color:var(--accent)] text-[color:var(--text-light)] border border-[color:var(--accent)] hover:bg-[color:var(--accent-hover)] hover:border-[color:var(--accent-hover)]',
            a.className,
          ]
            .filter(Boolean)
            .join(' ')}
        >
          {text}
        </a>
      );
    });
  }
  return actions;
}

export function PageHeader({
  title,
  subtitle,
  description,
  beta = false,
  actions,
  asOfDate,
  heroTitle,
  heroSubtitle,
  heroDescription,
  heroBeta = false,
  heroActions,
  heroImage,
  heroImageAlt = 'Fulcrum spiral background',
  className,
}: PageHeaderProps): JSX.Element {
  if (heroTitle) {
    const imageSrc = heroImage ?? LOCAL_HERO_IMAGE;
    return (
      <section
        className={[
          // Dark-bg banner, padded, overflow hidden.
          'relative overflow-hidden text-left bg-[color:var(--dark-bg)] text-[color:var(--text-light)] py-24 min-h-[400px]',
          className,
        ]
          .filter(Boolean)
          .join(' ')}
      >
        <div className="mx-auto max-w-[1200px] px-4 relative z-[2]">
          <div className="max-w-[90%]">
            <h1 className="font-[var(--font-heading)] text-[2rem] font-bold m-0 leading-[1.1]">
              {heroTitle}
              {heroBeta ? <BetaBadge size="lg" /> : null}
            </h1>
            {heroSubtitle ? (
              <h2 className="font-[var(--font-heading)] text-[1.5rem] font-semibold text-[color:var(--accent)] mx-0 my-2">
                {heroSubtitle}
              </h2>
            ) : null}
            {heroDescription ? (
              <p className="max-w-[900px] leading-[1.6] text-[1.1rem] opacity-90 mt-6 mb-8">
                {heroDescription}
              </p>
            ) : null}
            {heroActions && heroActions.length > 0 ? (
              <div className="flex flex-wrap gap-4">
                {heroActions.map((action, i) => {
                  const href = action.url ?? action.href ?? '#';
                  const text = action.text ?? action.label ?? '';
                  return (
                    <a
                      key={`${href}-${i}`}
                      href={href}
                      className={[
                        // Hero action button.
                        'inline-block rounded-full px-6 py-3 text-[0.9rem] font-medium no-underline border-2 transition-all',
                        action.active
                          ? 'bg-transparent border-[color:var(--accent)] text-[color:var(--accent)]'
                          : 'bg-[color:var(--accent)] border-transparent text-[color:var(--text-light)] hover:bg-transparent hover:border-[color:var(--accent)] hover:text-[color:var(--accent)] hover:-translate-y-0.5',
                        action.className,
                      ]
                        .filter(Boolean)
                        .join(' ')}
                    >
                      {text}
                    </a>
                  );
                })}
              </div>
            ) : null}
          </div>
        </div>
        <div className="absolute top-0 right-0 w-[40%] h-full pointer-events-none z-[1] hidden md:block">
          <img
            src={imageSrc}
            alt={heroImageAlt}
            className="block max-w-full h-auto opacity-80"
          />
        </div>
      </section>
    );
  }

  const resolvedDate = asOfDate ?? new Date();
  const actionNodes = renderActions(actions);
  return (
    <div
      className={[
        // 1200px centered container.
        'mx-auto max-w-[1200px] px-4 py-4',
        className,
      ]
        .filter(Boolean)
        .join(' ')}
    >
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          {title ? (
            <h2 className="font-[var(--font-heading)] text-[1.75rem] font-bold text-[color:var(--text-dark)] m-0 mb-2">
              {title}
              {beta ? <BetaBadge /> : null}
            </h2>
          ) : null}
          {subtitle ? (
            <p className="mt-1 text-base text-[color:var(--text-dark)] m-0">{subtitle}</p>
          ) : null}
          {description ? (
            <p className="mt-1 text-base text-[color:var(--text-dark)] opacity-80 m-0">
              {description}
            </p>
          ) : null}
          {/* "As of <date>" stamp. */}
          <p className="font-[var(--font-heading)] text-[0.9rem] text-[color:var(--gray-500)] m-0 mt-2 mb-6">
            As of <span>{formatLong(resolvedDate)}</span>
          </p>
        </div>
        {actionNodes ? (
          <div className="flex flex-wrap items-center gap-2">{actionNodes}</div>
        ) : null}
      </div>
    </div>
  );
}

export default PageHeader;
